// The six-field operator identity block (§2 / §5 / §14).
//
// One component, rendered on the login screen, in the sidebar and on the
// session page, so that "who am I signed in as" cannot say three different
// things in three places.
//
// ─── Every row here is the SERVER's answer ────────────────────────────────
//
//   Operator    ← session.ownerSubject, as the projection returned it
//   Signer      ← authority.signerAddress
//   Authority   ← authority.authorityVault
//   Membership  ← authority.membership
//   Chain       ← authority.authorityChainId
//   Device      ← authority.deviceId
//
// Not one of them is read from the connected wallet, from `REQUIRED_CHAIN_ID`,
// or from any other constant in this bundle. The console genuinely does not
// know its own authority — it repeats what it was told. That is the whole
// point: an authority the browser can compute is an authority the browser can
// forge, and a screenshot of it would prove nothing during acceptance.
//
// A legacy (pre-cutover, `auth_version` 1) session has no signer, no vault and
// no chain, because it was never minted against one. Those rows render "—" and
// the membership reads LEGACY. Filling them in from config would turn a real
// distinction into a cosmetic one.

import { type SessionMetadata } from "../auth/session";
import {
  chainLabel,
  isVaultBound,
  membershipLabel,
  shortAddress,
} from "../auth/session-claims";
import { Chip, Kv, KvList } from "../ui/primitives";

/** `title` gives the full value to anyone who needs to copy it; the visible
 *  text stays short so six rows fit a sidebar. */
function Mono({ value, title }: { value: string; title?: string }) {
  return (
    <code className="g-mono" title={title} style={{ fontSize: 11.5 }}>
      {value}
    </code>
  );
}

export function SessionIdentity({
  session,
  dense = false,
}: {
  session: SessionMetadata;
  dense?: boolean;
}) {
  const a = session.authority ?? null;
  const vaultBound = isVaultBound(a);

  return (
    // Marked so tests — and an operator's own DOM inspection during
    // acceptance — can scope to the identity block rather than to whichever
    // page happens to be hosting it.
    <div data-testid="session-identity">
    <KvList dense={dense}>
      <Kv k="Operator">
        <Mono value={session.ownerSubject} />
      </Kv>
      <Kv k="Signer">
        {a?.signerAddress ? (
          <Mono value={shortAddress(a.signerAddress)} title={a.signerAddress} />
        ) : (
          <span style={{ color: "var(--fg-dim)" }}>—</span>
        )}
      </Kv>
      <Kv k="Authority">
        {a?.authorityVault ? (
          <Mono value={shortAddress(a.authorityVault)} title={a.authorityVault} />
        ) : (
          <span style={{ color: "var(--fg-dim)" }}>—</span>
        )}
      </Kv>
      <Kv k="Membership">
        <Chip tone={vaultBound ? "ok" : "warn"} size="sm" solid>
          {membershipLabel(a?.membership ?? null)}
        </Chip>
      </Kv>
      <Kv k="Chain">
        <Mono value={chainLabel(a?.authorityChainId ?? null)} />
      </Kv>
      <Kv k="Device">
        {a?.deviceId ? (
          <Mono value={`${a.deviceId.slice(0, 12)}…`} title={a.deviceId} />
        ) : (
          <span style={{ color: "var(--fg-dim)" }}>—</span>
        )}
      </Kv>
    </KvList>
    </div>
  );
}
